import React, { useState, useRef, useCallback, useMemo, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { Bubble } from 'react-chartjs-2';
import 'chartjs-plugin-annotation';

import Img from '../../../../Img';
import useIsMobile from '../../../../hooks/useIsMobile';
import GameCardTooltip from '../GameCardTooltip';
import MatchCardCarousel from '../MatchCardCarousel';
import LandscapeScatterOverlay from '../LandscapeScatterOverlay';
import LockedChartOverlay from '../LockedChartOverlay';
import {
    InfoTrigger,
    InfoPopup,
    LegendSheet,
    ScatterLegendList,
    ConsistencyInfoBody,
} from './scatterInfo';
import {
    IMPACT_DOT_DIAMETERS,
    buildScatterData,
    buildScatterOptions,
    buildScatterMidlinePlugin,
    buildScatterCarouselCards,
    buildOverlappingCardModels,
    findOverlappingPointIndexes,
    clampTooltipTop,
    clampTooltipLeft,
    formatConsistencyLabel,
    CHART_CANVAS_HEIGHT_PX,
    COLOR_CONSISTENT,
    COLOR_OVER,
    COLOR_UNDER,
    COLOR_IMPACT,
} from '../performanceStatsUtils';
import usePerformanceStatsEngagement from '../usePerformanceStatsEngagement';
import { performanceStatsEngagementTypes } from '../../../../../utils/analytics';
import styles from '../performanceStats.scss';

export { InfoTrigger, InfoPopup, LegendSheet } from './scatterInfo';

// useLayoutEffect warns during SSR; fall back to useEffect on the server.
const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

function ScatterSection({ singles, currentUtr, profilePlayerId, isLocked, onLockClick, 'data-testid': dataTestId }) {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    const chartCardRef = useRef(null);
    const tooltipCardRef = useRef(null);
    const lastKeyRef = useRef(null);
    const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, cards: [] });
    const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });
    const [showAnalysisInfo, setShowAnalysisInfo] = useState(false);
    const [showConsistencyInfo, setShowConsistencyInfo] = useState(false);
    const [showLegendInfo, setShowLegendInfo] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedCardIndex, setSelectedCardIndex] = useState(0);
    const { isMobileWidth } = useIsMobile();
    const trackEngagement = usePerformanceStatsEngagement();

    const points = singles?.scatterplot?.data || [];

    const carouselEntries = useMemo(
        () => buildScatterCarouselCards(singles, profilePlayerId),
        [singles, profilePlayerId],
    );
    const showCarousel = isMobileWidth && !isLocked && carouselEntries.length > 0;

    useEffect(() => {
        // Cards read oldest → newest like the chart; land on the newest card.
        setSelectedCardIndex(Math.max(carouselEntries.length - 1, 0));
    }, [carouselEntries]);

    useEffect(() => {
        if (!showCarousel && isExpanded) {
            setIsExpanded(false);
        }
    }, [showCarousel, isExpanded]);

    const handleScatterTooltip = useCallback((model) => {
        const hasPoint = model.opacity !== 0 && model.dataPoints && model.dataPoints.length > 0;
        const key = hasPoint ? model.dataPoints[0].index : 'hidden';
        if (key === lastKeyRef.current) { return; }
        lastKeyRef.current = key;
        if (!hasPoint) {
            setTooltip(prev => (prev.visible ? { ...prev, visible: false } : prev));
            return;
        }
        const hoveredIndex = model.dataPoints[0].index;
        const pointModels = (chartRef.current?.chartInstance?.getDatasetMeta(0)?.data || [])
            .map(element => element?._model);
        const overlapping = findOverlappingPointIndexes(pointModels, hoveredIndex);
        setTooltip({
            visible: true,
            x: model.caretX,
            y: model.caretY,
            cards: buildOverlappingCardModels(points, overlapping.length ? overlapping : [hoveredIndex], profilePlayerId),
        });
    }, [points, profilePlayerId]);

    // Fires on every viewport so DS gets desktop parity; desktop gains no
    // behaviour, the carousel selection stays mobile-only.
    const handlePointClick = useCallback((elements) => {
        if (!elements?.length) { return; }
        trackEngagement(performanceStatsEngagementTypes.CLICK_RESULT);
        if (!showCarousel) { return; }
        const pointIndex = elements[0]._index;
        const cardIndex = carouselEntries.findIndex(entry => entry.pointIndex === pointIndex);
        if (cardIndex !== -1) {
            setSelectedCardIndex(cardIndex);
        }
    }, [carouselEntries, showCarousel, trackEngagement]);

    const selectedPointIndex = showCarousel
        ? (carouselEntries[selectedCardIndex]?.pointIndex ?? null)
        : null;

    const scatterData = useMemo(
        () => buildScatterData(singles, selectedPointIndex),
        [singles, selectedPointIndex],
    );
    const scatterOptions = useMemo(
        () => buildScatterOptions(singles, currentUtr, showCarousel ? null : handleScatterTooltip, isMobileWidth),
        [singles, currentUtr, showCarousel, handleScatterTooltip, isMobileWidth],
    );
    const scatterPlugins = useMemo(
        () => [buildScatterMidlinePlugin(currentUtr, isMobileWidth)],
        [currentUtr, isMobileWidth],
    );
    const consistency = formatConsistencyLabel(singles);
    const legendTestId = dataTestId && `modal.${dataTestId}.legend`;
    const analysisInfoTestId = dataTestId && `modal.${dataTestId}.analysis`;
    const consistencyInfoTestId = dataTestId && `modal.${dataTestId}.consistency`;

    useBrowserLayoutEffect(() => {
        if (!tooltip.visible || !tooltipCardRef.current) { return; }
        const { offsetWidth, offsetHeight } = tooltipCardRef.current;
        setTooltipSize(previous => (
            previous.width === offsetWidth && previous.height === offsetHeight
                ? previous
                : { width: offsetWidth, height: offsetHeight }
        ));
    }, [tooltip.visible, tooltip.cards]);

    const tooltipPosition = useMemo(() => {
        const wrapperWidth = canvasRef.current ? canvasRef.current.clientWidth : 0;
        const wrapperRect = canvasRef.current ? canvasRef.current.getBoundingClientRect() : null;
        const cardRect = chartCardRef.current ? chartCardRef.current.getBoundingClientRect() : null;
        const hasBounds = wrapperRect != null && cardRect != null;
        const top = clampTooltipTop({
            anchorY: tooltip.y,
            tooltipHeight: tooltipSize.height,
            boundsTop: hasBounds ? cardRect.top - wrapperRect.top : 0,
            boundsBottom: hasBounds ? cardRect.bottom - wrapperRect.top : CHART_CANVAS_HEIGHT_PX,
        });
        const left = clampTooltipLeft({
            anchorX: tooltip.x,
            tooltipWidth: tooltipSize.width,
            boundsWidth: wrapperWidth,
        });
        return {
            left: `${left}px`,
            top: `${top}px`,
        };
    }, [tooltip.x, tooltip.y, tooltipSize]);

    const chartCard = (
        <div className={styles.chartCard} ref={chartCardRef}>
            <div className={styles.chartHeaderRow} data-testid={dataTestId && `${dataTestId}.consistency`}>
                <span className={styles.chartHeaderTitleGroup}>
                    <span className={styles.chartHeaderLabel} data-testid={dataTestId && `${dataTestId}.consistency.label`}>Performance Consistency</span>
                    <InfoTrigger onOpen={() => setShowConsistencyInfo(true)} data-testid={dataTestId && `${dataTestId}.consistency.info-btn`} />
                </span>
                {consistency && (
                    <React.Fragment>
                        <span className={styles.chartHeaderValue} data-testid={dataTestId && `${dataTestId}.consistency.value`}>{consistency.value}</span>
                        {consistency.range && (
                            <span className={styles.chartHeaderRange} data-testid={dataTestId && `${dataTestId}.consistency.range`}>({consistency.range})</span>
                        )}
                    </React.Fragment>
                )}
            </div>
            <p className={styles.chartHeaderHint} data-testid={dataTestId && `${dataTestId}.hover-hint`}>
                {isMobileWidth ? 'Tap a dot or swipe cards for match details.' : 'Hover over dot for match details.'}
            </p>
            <div className={styles.chartCanvas} ref={canvasRef}>
                <Bubble
                    ref={chartRef}
                    // react-chartjs-2 2.x hands `plugins` to chart.js only at
                    // construction, so remount when the midline plugin's inputs
                    // change (isMobileWidth resolves just after first mount).
                    key={`scatter-${isMobileWidth ? 'mobile' : 'desktop'}-${currentUtr ?? 'none'}`}
                    data={scatterData}
                    options={scatterOptions}
                    plugins={scatterPlugins}
                    height={CHART_CANVAS_HEIGHT_PX}
                    onElementsClick={isLocked ? undefined : handlePointClick}
                />
                {!showCarousel && tooltip.visible && tooltip.cards.length > 0 && (
                    <GameCardTooltip ref={tooltipCardRef} cards={tooltip.cards} position={tooltipPosition} data-testid={dataTestId && `${dataTestId}.game-card`} />
                )}
            </div>
            {isMobileWidth ? (
                <div className={styles.legendTriggerRow}>
                    <button
                        type="button"
                        className={styles.legendTriggerBtn}
                        onClick={() => setShowLegendInfo(true)}
                        data-testid={dataTestId && `${dataTestId}.legend-trigger`}
                    >
                        Legend
                    </button>
                    {showCarousel && (
                        <button
                            type="button"
                            className={styles.expandTriggerBtn}
                            onClick={() => setIsExpanded(true)}
                            data-testid={dataTestId && `${dataTestId}.expand-btn`}
                        >
                            <Img src="/img/icons/rotate-to-landscape-icon.svg" alt="Expand chart" className={styles.expandTriggerIcon} data-testid={dataTestId && `${dataTestId}.expand-btn.icon`} />
                        </button>
                    )}
                </div>
            ) : (
                <div className={styles.legend} data-testid={dataTestId && `${dataTestId}.legend`}>
                    <span className={styles.legendItem} data-testid={dataTestId && `${dataTestId}.legend.consistent`}><span className={styles.legendDot} style={{ backgroundColor: COLOR_CONSISTENT }} />Consistent</span>
                    <span className={styles.legendItem} data-testid={dataTestId && `${dataTestId}.legend.over`}><span className={styles.legendDot} style={{ backgroundColor: COLOR_OVER }} />Over</span>
                    <span className={styles.legendItem} data-testid={dataTestId && `${dataTestId}.legend.under`}><span className={styles.legendDot} style={{ backgroundColor: COLOR_UNDER }} />Under</span>
                    <span className={styles.legendItem} data-testid={dataTestId && `${dataTestId}.legend.band`}><span className={styles.legendBand} />Consistency Band</span>
                    <span className={`${styles.legendItem} ${styles.legendItemImpact}`} data-testid={dataTestId && `${dataTestId}.legend.impact`}>
                        <span className={styles.legendImpactDots}>
                            {IMPACT_DOT_DIAMETERS.map(diameter => (
                                <span
                                    key={`impact-${diameter}`}
                                    className={styles.legendDot}
                                    style={{ backgroundColor: COLOR_IMPACT, width: `${diameter}px`, height: `${diameter}px` }}
                                />
                            ))}
                        </span>
                        {'Impact on UTR'}
                    </span>
                </div>
            )}
        </div>
    );

    return (
        <section className={styles.chartBlock} data-testid={dataTestId}>
            <div className={styles.sectionHeader}>
                <h4 className={styles.sectionTitle} data-testid={dataTestId && `${dataTestId}.title`}>Match Performance Analysis</h4>
                <InfoTrigger onOpen={() => setShowAnalysisInfo(true)} data-testid={dataTestId && `${dataTestId}.info-btn`} />
            </div>
            <p className={styles.sectionSubtitle} data-testid={dataTestId && `${dataTestId}.subtitle`}>
                Each dot represents your match performance and its impact on your rating.
            </p>
            {isLocked ? (
                <div className={styles.lockedSection}>
                    <div className={styles.lockedBlur}>
                        {chartCard}
                    </div>
                    <LockedChartOverlay onClick={onLockClick} data-testid={dataTestId && `${dataTestId}.lock-btn`} />
                </div>
            ) : chartCard}
            {showCarousel && (
                <MatchCardCarousel
                    entries={carouselEntries}
                    selectedIndex={selectedCardIndex}
                    onSelect={setSelectedCardIndex}
                    data-testid={dataTestId && `${dataTestId}.carousel`}
                />
            )}
            <InfoPopup
                isOpen={showAnalysisInfo}
                onClose={() => setShowAnalysisInfo(false)}
                title="About Match Performance Analysis"
                data-testid={analysisInfoTestId}
            >
                <p data-testid={analysisInfoTestId && `${analysisInfoTestId}.intro`}>The chart shows matches from the past 12 months that count toward your Fully Reliable Verified UTR Singles Rating.</p>
                <ul data-testid={analysisInfoTestId && `${analysisInfoTestId}.points`}>
                    <li data-testid={analysisInfoTestId && `${analysisInfoTestId}.points.horizontal-axis`}>The horizontal axis indicates the 12 month timeline.</li>
                    <li data-testid={analysisInfoTestId && `${analysisInfoTestId}.points.vertical-axis`}>The vertical axis indicates how much you over or underperformed your rating today in each match, increasing from bottom to top.</li>
                    <li data-testid={analysisInfoTestId && `${analysisInfoTestId}.points.blue-section`}>The blue section denotes those matches for which you performed within your Performance Consistency range.</li>
                    <li data-testid={analysisInfoTestId && `${analysisInfoTestId}.points.smaller-range`}>A smaller range indicates a higher level of consistency across your match performances.</li>
                    <li data-testid={analysisInfoTestId && `${analysisInfoTestId}.points.green-dots`}>The green dots denote those matches for which you performed above your Performance Consistency range.</li>
                    <li data-testid={analysisInfoTestId && `${analysisInfoTestId}.points.red-dots`}>The red dots denote those matches for which you performed below your Performance Consistency range.</li>
                </ul>
            </InfoPopup>
            <InfoPopup
                isOpen={showConsistencyInfo}
                onClose={() => setShowConsistencyInfo(false)}
                title="About Performance Consistency"
                data-testid={consistencyInfoTestId}
            >
                <ConsistencyInfoBody data-testid={consistencyInfoTestId} />
            </InfoPopup>
            <LegendSheet
                isOpen={showLegendInfo}
                onClose={() => setShowLegendInfo(false)}
                data-testid={legendTestId}
            >
                <ScatterLegendList data-testid={legendTestId} />
            </LegendSheet>
            {isExpanded && showCarousel && (
                <LandscapeScatterOverlay
                    singles={singles}
                    currentUtr={currentUtr}
                    consistency={consistency}
                    entries={carouselEntries}
                    selectedIndex={selectedCardIndex}
                    onSelect={setSelectedCardIndex}
                    onClose={() => setIsExpanded(false)}
                    data-testid={dataTestId && `modal.${dataTestId}.landscape`}
                />
            )}
        </section>
    );
}

ScatterSection.propTypes = {
    singles: PropTypes.shape({
        scatterplot: PropTypes.shape({
            data: PropTypes.arrayOf(PropTypes.shape({})),
        }),
    }),
    currentUtr: PropTypes.number,
    profilePlayerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isLocked: PropTypes.bool,
    onLockClick: PropTypes.func,
    'data-testid': PropTypes.string,
};

ScatterSection.defaultProps = {
    singles: null,
    currentUtr: null,
    profilePlayerId: null,
    isLocked: false,
    onLockClick: null,
    'data-testid': null,
};

export default ScatterSection;
